import { useRecoilValue } from 'recoil';

import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import useGetMyUncheckedAiAnswer from '@/hooks/qna/mobile/home/useGetMyUncheckedAiAnswer';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import useNavigate from '@/hooks/useNavigate';
import { tokenState } from '@/recoil/atoms/common/auth';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { EAirbridgeLinkType } from '@/shared/types';
import { AiAnswerStatus, QuestionStatus } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { isWebView } from '@/utils/mobile/RNPlugins';

export type TrackingInfo = {
  buttonText?: string;
  qnaId?: string;
  qnaTitle?: string;
  qnaCategory?: string;
  qnaSubCategory?: string;
  linkType?: EAirbridgeLinkType;
};

const useCanProgressQnaWrite = () => {
  const token = useRecoilValue(tokenState);
  const { moveToPage } = useCustomRouter();
  const { setShowAuthModal, setTrackInfo } = useAuthModal();
  const remainAiAnswer = useGetMyUncheckedAiAnswer();
  const { showModal, closeModal } = useModal();
  const navigate = useNavigate();
  const airbridgeDynamicLink = useAirbridgeDynamicLink();

  const onClickQuestionDetail = (questionPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.AI.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.AI.appScreenName,
      params: {
        questionPid,
      },
    });

    closeModal();
  };

  const canProgressQnaWrite = (aiAnswerStatus: AiAnswerStatus, status: QuestionStatus) => {
    if (
      (aiAnswerStatus === AiAnswerStatus.GenerationComplete ||
        aiAnswerStatus === AiAnswerStatus.Generating ||
        aiAnswerStatus === AiAnswerStatus.GenerationRequested ||
        aiAnswerStatus === AiAnswerStatus.GenerationFailed) &&
      status !== QuestionStatus.PaymentFailed
    ) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '기존 답변을 먼저 확인해 주세요',
          message: '고민이 해결됐는지 알려주신 뒤 다음 질문을 작성할 수 있어요.',
          buttons: [
            {
              label: '닫기',
              onClick: () => closeModal(),
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '답변 보기',
              onClick: () => onClickQuestionDetail(remainAiAnswer?.questionPid ?? ''),
            },
          ],
        },
      });

      return false;
    }

    return true;
  };

  const clickProgressWrite = async (trackInfo?: {
    content: string;
    buttonText: string;
  }) => {
    if (!token) {
      // 로그인 정보가 없는 경우
      setShowAuthModal(true);
      setTrackInfo({
        campaign: 'web_content_qna',
        content: trackInfo?.content ?? '',
        buttonText: trackInfo?.buttonText ?? '',
      });
    } else {
      // 로그인 정보가 있는 경우
      if (isWebView()) {
        if (remainAiAnswer && !canProgressQnaWrite(remainAiAnswer.aiAnswerStatus, remainAiAnswer.status)) {
          return;
        }
      }
    }
    navigate.navigateToQnaWrite();
  };

  // webToApp을 위한 임시 처리
  const guideToAppDownloadTemporary = async (isNeedToApp = false, trackingInfo?: TrackingInfo) => {
    const isLogin = !!token;

    const {
      buttonText,
      qnaCategory,
      qnaId,
      qnaTitle,
      linkType = EAirbridgeLinkType.GENERAL,
      qnaSubCategory,
    } = trackingInfo ?? {};

    if (!isLogin || isNeedToApp) {
      // 로그인 정보가 없거나 앱으로 이동해야하는 경우
      airbridgeDynamicLink.navigateWithTracking(
        DeeplinkLoggerEventName.ClickCtaButton,
        {
          campaign: 'web_content_qna',
          content: 'web_content_flt_details',
          term: buttonText,
          sub_id: qnaId,
          sub_id_1: qnaTitle,
          sub_id_2: qnaCategory,
          sub_id_3: qnaSubCategory,
        },
        linkType
      );
      return;
    }

    // 로그인 정보가 있는 경우
    // 웹뷰인 경우
    if (isWebView()) {
      if (remainAiAnswer && !canProgressQnaWrite(remainAiAnswer.aiAnswerStatus, remainAiAnswer.status)) {
        return;
      }
    }
    navigate.navigateToQnaWrite();
  };

  return {
    clickProgressWrite,
    guideToAppDownloadTemporary,
  };
};

export default useCanProgressQnaWrite;
